import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

/* Modals */
import ConfirmationModal from '../cards/ConfirmationModal';
import ShareModal from '../share/ShareModal';

/* Context */
import { useAuthContext } from '../../context/AuthContext';

/* CSS */
import shared from '../../styles/shared.module.css';

const TripContextMenu = ({ trip, position, triggerRef, onClose, onDuplicate, onDelete }) => {

    const navigate = useNavigate();

    const menuRef = useRef(null);
    const { currentUser } = useAuthContext();

    const currentMember = trip.members?.find(m => m.user_id === currentUser.id);
    const access = { isMember: !!currentMember, role: currentMember?.role || null };

    const [showShareModal, setShowShareModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    // A ref (not state) is required here because the event listeners below close over
    // the initial render and cannot see updated state values
    const deleteModalOpenRef = useRef(false);
    const shareModalOpenRef = useRef(false);

    useEffect(() => {
        // Guard: don't close the component while the delete modal is open
        const handleClose = () => {
            if (!deleteModalOpenRef.current && !shareModalOpenRef.current) onClose();
        };
        const handleMouseDown = (e) => {
            if (deleteModalOpenRef.current || shareModalOpenRef.current) return;
            // triggerRef (the 3-dot button in TripCard) is excluded so that clicking it
            // doesn't fire onClose here AND toggle the menu open again in the parent.
            const clickedTrigger = triggerRef?.current?.contains(e.target);
            const clickedMenu = menuRef.current?.contains(e.target);
            if (!clickedTrigger && !clickedMenu) onClose();
        };

        window.addEventListener('scroll', handleClose, true);
        window.addEventListener('resize', handleClose);
        document.addEventListener('mousedown', handleMouseDown);

        return () => {
            window.removeEventListener('scroll', handleClose, true);
            window.removeEventListener('resize', handleClose);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [onClose, triggerRef]);

    const handleOpenTrip = () => {
        navigate(`/trip/${trip.id}`);
        onClose();
    };

    const handleDuplicate = () => {
        if (onDuplicate) onDuplicate(trip.id);
        onClose();
    };

    // Handle clicking buttons
    const handleDeleteClick = () => {
        // Set the ref before state so listeners see it immediately (state update is async)
        deleteModalOpenRef.current = true;
        setShowDeleteModal(true);
    };

    const handleShareClick = () => {
        shareModalOpenRef.current = true;
        setShowShareModal(true);
    };

    // Handle clicking confirm delete trip
    const handleConfirmDelete = () => {
        if (onDelete) onDelete(trip.id);
        deleteModalOpenRef.current = false;
        setShowDeleteModal(false);
        onClose();
    };

    // Handle modal close
    const handleDeleteModalClose = () => {
        deleteModalOpenRef.current = false;
        setShowDeleteModal(false);
        onClose();
    };

    const handleShareModalClose = () => {
        shareModalOpenRef.current = false;
        setShowShareModal(false);
        onClose();
    };

    return (
        <>
            {/* Hidden while modal is open — component stays mounted to preserve modal state */}
            {!showDeleteModal && !showShareModal && createPortal(
                <div
                    ref={menuRef}
                    className={shared.tripContextMenu}
                    style={{ position: 'fixed', top: position.y, left: position.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={shared.tripMenuItem} onClick={handleOpenTrip}>Open trip</div>
                    <div className={shared.tripMenuItem} onClick={handleDuplicate}>Duplicate trip</div>
                    <div className={shared.tripMenuItem} onClick={handleShareClick}>Share trip</div>
                    <div className={shared.tripMenuSeparator} />
                    <div className={`${shared.tripMenuItem} ${shared.tripMenuItemDanger}`} onClick={handleDeleteClick}>Delete trip</div>
                </div>,
                document.body
            )}

            {showDeleteModal &&
                <ConfirmationModal
                    isOpen={showDeleteModal}
                    title={`Delete "${trip.title}"?`}
                    message="Are you sure you want to delete this trip? This action cannot be undone."
                    onClose={handleDeleteModalClose}
                    onConfirm={handleConfirmDelete}
                    confirmText="Delete"
                    cancelText="Cancel"
                    isDanger={true}
                    showCancel={true}
                />
            }

            {showShareModal &&
                <ShareModal
                    isOpen={showShareModal}
                    onClose={handleShareModalClose}
                    tripId={trip.id}
                    tripTitle={trip.title}
                    access={access}
                />
            }
        </>
    );
};

export default TripContextMenu;
